/**
 * @author Nihar<nihar.baishya@314ecorp.com>
 * @description batch upload header mapping schema
 */

import _ from 'lodash';

import { BATCH_UPLOAD_HEADER_MAPPING_FIELDS_CONFIG, OPTIONAL_PATIENT_FIELD_KEYS } from 'app-constants/eligibility';

const formWrapperClass = 'batch-upload-header-mapping-card';
const formSectionClass = `${formWrapperClass}-form-section`;
const patientFieldsSectionClass = `${formSectionClass} ${formWrapperClass}-patient-fields-section`;

// schema keys for batch header mapping form
const schemaKeys: Record<EligibilityBatchUpload.IHeaderMappingSchemaKeys, EligibilityBatchUpload.IHeaderMappingSchemaKeys> = {
	payer: 'payer',
	provider: 'provider',
	memberId: 'memberId',
	patientFirstName: 'patientFirstName',
	patientLastName: 'patientLastName',
	patientDOB: 'patientDOB',
	serviceStartDate: 'serviceStartDate',
	serviceEndDate: 'serviceEndDate',
	location: 'location',
	traceNumber: 'traceNumber',
	traceEntityId: 'traceEntityId',
	// state: 'state',
	gender: 'gender',
	relationshipToSubscriber: 'relationshipToSubscriber',
	ssn: 'ssn',
	medicaidId: 'medicaidId',
	groupNumber: 'groupNumber',
};

// schema key vs submit key mapping
const schemaKeyVsSubmitKeyMapping: Record<
	EligibilityBatchUpload.IHeaderMappingSchemaKeys,
	keyof EligibilityBatchUpload.IHeaderMappingSubmitModel
> = {
	payer: 'Payer',
	provider: 'Provider',
	memberId: 'Member ID',
	patientFirstName: 'Patient First Name',
	patientLastName: 'Patient Last Name',
	patientDOB: 'Patient DOB',
	serviceStartDate: 'Service Start Date',
	serviceEndDate: 'Service End Date',
	location: 'Location',
	traceNumber: 'Trace Number',
	traceEntityId: 'Trace Entity ID',
	// state: 'State',
	gender: 'Gender',
	relationshipToSubscriber: 'Relationship To Subscriber',
	ssn: 'SSN',
	medicaidId: 'Medicaid ID',
	groupNumber: 'Group Number',
};

// helper to create a select field schema entry
interface IFieldOverrides {
	'x-display'?: 'none' | 'visible' | 'hidden';
	'x-decorator'?: string;
	'x-decorator-props'?: Record<string, unknown>;
	'x-index'?: number;
	'x-validator'?: Record<string, unknown>[];
}

const createSelectField = (
	key: EligibilityBatchUpload.IHeaderMappingSchemaKeys,
	options: IMultiSelectOption[],
	overrides?: IFieldOverrides,
): Record<string, unknown> => {
	const config = BATCH_UPLOAD_HEADER_MAPPING_FIELDS_CONFIG[key];
	return {
		type: 'string',
		title: config.title,
		'x-component': 'Select',
		'x-component-props': {
			'data-testid': `batch-header-select-${key}`,
			placeholder: config.placeholder,
			allowClear: true,
			showSearch: true,
			options,
		},
		'x-decorator': 'FormItem',
		'x-decorator-props': {
			tooltip: config.tooltip,
			...overrides?.['x-decorator-props'],
		},
		...(config.errorMessage
			? {
					'x-validator': [{ required: true, message: config.errorMessage }],
				}
			: {}),
		..._.omit(overrides, 'x-decorator-props'),
	};
};

interface ISchemaOptions {
	optionalPatientFieldOrder?: EligibilityBatchUpload.IOptionalPatientFieldKey[];
	onOptionalPatientFieldAdd?: (fieldKey: EligibilityBatchUpload.IOptionalPatientFieldKey) => void;
	onOptionalPatientFieldRemove?: (fieldKey: EligibilityBatchUpload.IOptionalPatientFieldKey) => void;
}

const getOptionalPatientFieldProperties = (
	options: IMultiSelectOption[],
	optionalPatientFieldOrder: EligibilityBatchUpload.IOptionalPatientFieldKey[],
	onOptionalPatientFieldRemove?: (fieldKey: EligibilityBatchUpload.IOptionalPatientFieldKey) => void,
): Record<string, unknown> => {
	const orderedOptionalPatientFields = _.uniq([
		...optionalPatientFieldOrder,
		..._.difference(OPTIONAL_PATIENT_FIELD_KEYS, optionalPatientFieldOrder),
	]);

	return _.fromPairs(
		_.map(orderedOptionalPatientFields, (fieldKey, index) => [
			fieldKey,
			createSelectField(fieldKey, options, {
				'x-display': _.includes(optionalPatientFieldOrder, fieldKey) ? 'visible' : 'none',
				'x-decorator': 'RemovableFormItem',
				'x-decorator-props': {
					onFieldRemove: onOptionalPatientFieldRemove,
				},
				'x-index': index + 4,
			}),
		]),
	);
};

// get schema for batch upload header mapping form
const getSchema = (options: IMultiSelectOption[], schemaOptions: ISchemaOptions = {}): Record<string, AnyType> => ({
	title: 'Batch Upload Header Mapping',
	type: 'object',
	properties: {
		formWrapper: {
			type: 'void',
			'x-component': 'Card',
			'x-component-props': {
				'data-testid': 'batch-header-card',
				className: formWrapperClass,
				title: (
					<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
						<div style={{ width: '55%' }}>{'Veritable Header'}</div>
						<div style={{ width: '45%' }}>{'Uploaded Header'}</div>
					</div>
				),
			},
			properties: {
				payerProviderFields: {
					type: 'void',
					'x-component': 'Card',
					'x-component-props': {
						'data-testid': 'batch-header-section-payerProvider',
						className: formSectionClass,
						title: 'Payer and Provider Fields',
					},
					properties: {
						[schemaKeys.payer]: createSelectField('payer', options),
						[schemaKeys.provider]: createSelectField('provider', options),
					},
				},
				patientFields: {
					type: 'void',
					'x-component': 'Card',
					'x-component-props': {
						'data-testid': 'batch-header-section-patient',
						className: patientFieldsSectionClass,
						title: 'Patient Fields',
					},
					properties: {
						[schemaKeys.memberId]: createSelectField('memberId', options, { 'x-index': 0 }),
						[schemaKeys.patientFirstName]: createSelectField('patientFirstName', options, { 'x-index': 1 }),
						[schemaKeys.patientLastName]: createSelectField('patientLastName', options, { 'x-index': 2 }),
						[schemaKeys.patientDOB]: createSelectField('patientDOB', options, { 'x-index': 3 }),
						...getOptionalPatientFieldProperties(
							options,
							schemaOptions.optionalPatientFieldOrder ?? [],
							schemaOptions.onOptionalPatientFieldRemove,
						),
						addFieldButton: {
							type: 'void',
							'x-component': 'AddOptionalFieldButton',
							'x-component-props': {
								onFieldAdd: schemaOptions.onOptionalPatientFieldAdd,
							},
							'x-index': OPTIONAL_PATIENT_FIELD_KEYS.length + 4,
						},
					},
				},
				encounterFields: {
					type: 'void',
					'x-component': 'Card',
					'x-component-props': {
						'data-testid': 'batch-header-section-encounter',
						className: formSectionClass,
						title: 'Encounter Fields',
					},
					properties: {
						[schemaKeys.serviceStartDate]: createSelectField('serviceStartDate', options),
						[schemaKeys.serviceEndDate]: createSelectField('serviceEndDate', options),
						[schemaKeys.location]: createSelectField('location', options),
					},
				},
				additionalFields: {
					type: 'void',
					'x-component': 'Card',
					'x-component-props': {
						'data-testid': 'batch-header-section-additional',
						className: formSectionClass,
						title: 'Additional Fields',
					},
					properties: {
						[schemaKeys.traceNumber]: createSelectField('traceNumber', options),
						[schemaKeys.traceEntityId]: createSelectField('traceEntityId', options),
					},
				},
			},
		},
	},
});

export { schemaKeys, schemaKeyVsSubmitKeyMapping };
export default getSchema;
